<template>
    <div class="tooltip-wrapper" @click.stop="toggleTooltip">
        <span class="cost-label">
            {{ displayLabel }}
            <svg
                class="info-icon-svg"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
            >
                <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                />
            </svg>
        </span>

        <div
            v-if="hasContent"
            class="tooltip-content"
            :class="{
                'is-visible': tooltipVisible,
                'shipping-tooltip':
                    shippingMethodCharge > 0 ||
                    (getShippingServices() && getShippingServices().length > 0),
                'packaging-tooltip':
                    getPackagingServices() && getPackagingServices().length > 0,
                'sales-tax-tooltip':
                    salesTax > 0 ||
                    (getSalesTaxServices() && getSalesTaxServices().length > 0)
            }"
        >
            <div class="tooltip-header">
                <h4>{{ displayTitle }}</h4>
                <svg
                    class="close-icon"
                    @click.stop="closeTooltip"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </div>
            <div class="tooltip-body">
                <table class="base-fee-table">
                    <thead>
                        <tr>
                            <th>Fee</th>
                            <th v-if="isBaseFeeBreakdown">Color</th>
                            <th>Qty</th>
                            <th>Amt</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <!-- Color-Size Combinations -->
                        <template
                            v-if="
                                colorSizeCombinations &&
                                colorSizeCombinations.length > 0
                            "
                        >
                            <tr
                                v-for="(
                                    combination, index
                                ) in colorSizeCombinations"
                                :key="'color-size-' + index"
                            >
                                <td>
                                    Product Base Price -
                                    {{ combination.size || "N/A" }}
                                </td>
                                <td v-if="isBaseFeeBreakdown">
                                    <div class="color-box-wrapper">
                                        <div
                                            class="color-box"
                                            :style="{
                                                backgroundColor:
                                                    getColorValue(combination)
                                            }"
                                            :title="getColorName(combination)"
                                        ></div>
                                    </div>
                                </td>
                                <td>{{ combination.quantity || 0 }}</td>
                                <td>
                                    {{
                                        formatPrice(
                                            getColorSizeUnitPrice(combination)
                                        )
                                    }}
                                </td>
                                <td>
                                    {{
                                        formatPrice(
                                            getColorSizeTotalPrice(combination)
                                        )
                                    }}
                                </td>
                            </tr>
                        </template>

                        <!-- Product Base Price (if provided and no color-size combinations) -->
                        <tr
                            v-if="
                                (basePrice > 0 || getBaseUnitPrice() > 0) &&
                                (!colorSizeCombinations ||
                                    colorSizeCombinations.length === 0)
                            "
                        >
                            <td>
                                {{ basePriceLabel || "Product Base Price" }}
                            </td>
                            <td v-if="isBaseFeeBreakdown">-</td>
                            <td>{{ quantity }}</td>
                            <td>{{ formatPrice(getBaseUnitPrice()) }}</td>
                            <td>
                                {{ formatPrice(getBaseUnitPrice() * quantity) }}
                            </td>
                        </tr>

                        <!-- Setup Fee Details (if provided) -->
                        <template
                            v-if="
                                (setupFeeDetails &&
                                    setupFeeDetails.locations &&
                                    setupFeeDetails.locations.length > 0) ||
                                (getSetupFeeServices() &&
                                    getSetupFeeServices().length > 0)
                            "
                        >
                            <template
                                v-if="
                                    setupFeeDetails &&
                                    setupFeeDetails.locations &&
                                    setupFeeDetails.locations.length > 0
                                "
                            >
                                <template
                                    v-for="(
                                        location, index
                                    ) in setupFeeDetails.locations"
                                >
                                    <tr
                                        v-if="
                                            parseFloat(location.total || 0) > 0
                                        "
                                        :key="'location-' + index"
                                    >
                                        <td>
                                            {{
                                                location.name ||
                                                `Location ${index + 1}`
                                            }}
                                        </td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td>{{ location.quantity || 1 }}</td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    location.per_price || 0
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(location.total || 0)
                                            }}
                                        </td>
                                    </tr>
                                </template>
                            </template>

                            <!-- Services with rollup_fee_id = 2 (Setup Fee services) -->
                            <template v-for="service in getSetupFeeServices()">
                                <!-- Only show services with total > 0 -->
                                <template v-if="getServiceTotal(service) > 0">
                                    <!-- Services with show_service = false (grayed out, but show actual price) -->
                                    <tr
                                        v-if="!service.show_service"
                                        :key="
                                            'setup-service-incl-' + service.id
                                        "
                                        class="disable_data"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td v-if="isPerItemService(service)">
                                            {{ quantity }}
                                        </td>
                                        <td v-else>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Services with show_service = true (normal display) -->
                                    <tr
                                        v-else
                                        :key="'setup-service-' + service.id"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td v-if="isPerItemService(service)">
                                            {{ quantity }}
                                        </td>
                                        <td v-else>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>
                                </template>
                            </template>

                            <!-- Total Row for Setup Fee -->
                            <tr v-if="setupFeeTotal > 0" class="total-row">
                                <td :colspan="isBaseFeeBreakdown ? 4 : 3">
                                    <strong>Total</strong>
                                </td>
                                <td>
                                    <strong>{{
                                        formatPrice(setupFeeTotal)
                                    }}</strong>
                                </td>
                            </tr>
                        </template>

                        <!-- Rush Fee Breakdown -->
                        <template
                            v-if="
                                rushFeeBreakdown &&
                                rushFeeBreakdown.services &&
                                rushFeeBreakdown.services.length > 0
                            "
                        >
                            <tr
                                v-for="(
                                    service, index
                                ) in rushFeeBreakdown.services"
                                :key="'rush-fee-' + index"
                            >
                                <td>
                                    {{ getRushServiceNameWithDate(service) }}
                                </td>
                                <td v-if="isBaseFeeBreakdown">-</td>
                                <td>
                                    {{ getRushFeeServiceQuantity(service) }}
                                </td>
                                <td>
                                    {{ formatPrice(service.retail_cost || 0) }}
                                </td>
                                <td>
                                    {{
                                        formatPrice(
                                            getRushFeeServiceTotal(service)
                                        )
                                    }}
                                </td>
                            </tr>

                            <!-- Total Row for Rush Fee -->
                            <tr v-if="rushFeeTotal > 0" class="total-row">
                                <td :colspan="isBaseFeeBreakdown ? 4 : 3">
                                    <strong>Total</strong>
                                </td>
                                <td>
                                    <strong>{{
                                        formatPrice(rushFeeTotal)
                                    }}</strong>
                                </td>
                            </tr>
                        </template>

                        <!-- Patch Cost Breakdown (with Inventory Adjustment) -->
                        <template
                            v-if="
                                patchCostDetails &&
                                patchCostDetails.length > 0
                            "
                        >
                            <template v-for="(row, pIdx) in patchCostDetails">
                                <!-- Base Patch Row (only when charged patches exist) -->
                                <tr
                                    v-if="
                                        parseInt(
                                            row.charged_quantity || 0,
                                            10
                                        ) > 0
                                    "
                                    :key="'patch-base-' + pIdx"
                                >
                                    <td>
                                        {{ row.patchName || 'Patch' }}{{ row.location ? ' (' + row.location + ')' : '' }}
                                    </td>
                                    <td v-if="isBaseFeeBreakdown">-</td>
                                    <td>{{ row.slab || '-' }}</td>
                                    <td>
                                        {{ formatPrice(parseFloat(row.perPatchPrice || 0)) }}
                                    </td>
                                    <td>
                                        {{ formatPrice((parseFloat(row.perPatchPrice || 0) * (parseInt(row.slab || 0, 10) || 0))) }}
                                    </td>
                                </tr>
                                <!-- Inventory Row (negative only when inventory reduces charges) -->
                                <tr
                                    v-if="parseInt(row.inventory_used || 0, 10) > 0"
                                    :key="'patch-inv-' + pIdx"
                                >
                                    <td>Inventory</td>
                                    <td v-if="isBaseFeeBreakdown">-</td>
                                    <td>
                                        {{
                                            parseInt(
                                                row.charged_quantity || 0,
                                                10
                                            ) > 0
                                                ? '-' + row.inventory_used
                                                : row.inventory_used
                                        }}
                                    </td>
                                    <td>
                                        {{ formatPrice(parseFloat(row.perPatchPrice || 0)) }}
                                    </td>
                                    <td
                                        class="font-weight-bold"
                                        :class="{
                                            'text-danger':
                                                parseInt(row.charged_quantity || 0, 10) > 0
                                        }"
                                    >
                                        {{
                                            formatPrice(
                                                (parseInt(
                                                    row.charged_quantity || 0,
                                                    10
                                                ) > 0
                                                    ? -1
                                                    : 1) *
                                                    (parseFloat(row.perPatchPrice || 0) *
                                                        (parseInt(
                                                            row.inventory_used || 0,
                                                            10
                                                        ) || 0))
                                            )
                                        }}
                                    </td>
                                </tr>
                            </template>

                            <!-- Total Row for Patch Cost (Charged Total) -->
                            <tr
                                v-if="
                                    patchCostDetails &&
                                    patchCostDetails.length > 0
                                "
                                class="total-row"
                            >
                                <td :colspan="isBaseFeeBreakdown ? 4 : 3">
                                    <strong>Total</strong>
                                </td>
                                <td>
                                    <strong>{{
                                        formatPrice(patchCostTotal)
                                    }}</strong>
                                </td>
                            </tr>
                        </template>

                        <!-- Run Charge Breakdown (for decoration type 0, 1, and 2) -->
                        <template
                            v-if="
                                runChargeBreakdown &&
                                runChargeBreakdown.length > 0
                            "
                        >
                            <!-- Handle location-wise structure (new format) -->
                            <template
                                v-if="
                                    runChargeBreakdown[0] &&
                                    runChargeBreakdown[0].location &&
                                    runChargeBreakdown[0].items
                                "
                            >
                                <template
                                    v-for="(
                                        location, locIndex
                                    ) in runChargeBreakdown"
                                >
                                    <template
                                        v-for="(
                                            row, rowIndex
                                        ) in location.items"
                                    >
                                        <tr
                                            v-if="
                                                row.total === 'INCL' ||
                                                row.total === '-' ||
                                                parseFloat(row.total || 0) > 0
                                            "
                                            :key="
                                                'runcharge-' +
                                                locIndex +
                                                '-' +
                                                rowIndex
                                            "
                                        >
                                            <td>
                                                <strong
                                                    v-if="
                                                        row.type === 'location'
                                                    "
                                                    >{{ row.name }}</strong
                                                >
                                                <span v-else>{{
                                                    row.name
                                                }}</span>
                                            </td>
                                            <td v-if="isBaseFeeBreakdown">-</td>
                                            <td>
                                                {{
                                                    row.qty === "INCL" ||
                                                    row.qty === "-"
                                                        ? row.qty
                                                        : row.qty
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    row.amt === "INCL" ||
                                                    row.amt === "-"
                                                        ? row.amt
                                                        : formatPrice(row.amt)
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    row.total === "INCL" ||
                                                    row.total === "-"
                                                        ? row.total
                                                        : formatPrice(row.total)
                                                }}
                                            </td>
                                        </tr>
                                    </template>
                                </template>
                            </template>
                            <!-- Handle flat array structure (old format - backward compatibility) -->
                            <template v-else>
                                <template
                                    v-for="(
                                        row, rowIndex
                                    ) in runChargeBreakdown"
                                >
                                    <tr
                                        v-if="
                                            row.total === 'INCL' ||
                                            row.total === '-' ||
                                            parseFloat(row.total || 0) > 0
                                        "
                                        :key="'runcharge-' + rowIndex"
                                    >
                                        <td>
                                            <strong
                                                v-if="row.type === 'location'"
                                                >{{ row.name }}</strong
                                            >
                                            <span v-else>{{ row.name }}</span>
                                        </td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td>
                                            {{
                                                row.qty === "INCL" ||
                                                row.qty === "-"
                                                    ? row.qty
                                                    : row.qty
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                row.amt === "INCL" ||
                                                row.amt === "-"
                                                    ? row.amt
                                                    : formatPrice(row.amt)
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                row.total === "INCL" ||
                                                row.total === "-"
                                                    ? row.total
                                                    : formatPrice(row.total)
                                            }}
                                        </td>
                                    </tr>
                                </template>
                            </template>

                            <!-- Services with rollup_fee_id = 12 (Run Charge services) -->
                            <template v-for="service in getRunChargeServices()">
                                <!-- Only show services with total > 0 -->
                                <template v-if="getServiceTotal(service) > 0">
                                    <!-- Services with show_service = false (grayed out, but show price) -->
                                    <tr
                                        v-if="!service.show_service"
                                        :key="
                                            'runcharge-service-incl-' +
                                            service.id
                                        "
                                        class="disable_data"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td v-if="isPerItemService(service)">
                                            {{ getServiceQuantity(service) }}
                                        </td>
                                        <td v-else>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Services with show_service = true (normal display) -->
                                    <tr
                                        v-else
                                        :key="'runcharge-service-' + service.id"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>
                                </template>
                            </template>

                            <!-- Total Row for Run Charge Breakdown -->
                            <tr class="total-row">
                                <td :colspan="isBaseFeeBreakdown ? 4 : 3">
                                    <strong>Total</strong>
                                </td>
                                <td>
                                    <strong>{{
                                        formatPrice(
                                            getRunChargeBreakdownTotal()
                                        )
                                    }}</strong>
                                </td>
                            </tr>
                        </template>

                        <!-- Custom Color Fee Breakdown (for decoration type 0, 1, and 2) -->
                        <template
                            v-if="
                                (colorMixFeeData &&
                                    colorMixFeeData !== null &&
                                    parseFloat(colorMixFeeData.total || 0) >
                                        0) ||
                                (getColorMixFeeServices() &&
                                    getColorMixFeeServices().length > 0)
                            "
                        >
                            <!-- Traditional Color Mix Fee Data (only show if total > 0) -->
                            <tr
                                v-if="
                                    colorMixFeeData &&
                                    colorMixFeeData !== null &&
                                    parseFloat(colorMixFeeData.total || 0) > 0
                                "
                            >
                                <td>
                                    <strong>{{ getColorMixFeeLabel() }}</strong>
                                </td>
                                <td v-if="isBaseFeeBreakdown">-</td>
                                <td>
                                    {{ colorMixFeeData.mixColorCount || 0 }}
                                </td>
                                <td>
                                    {{
                                        formatPrice(
                                            colorMixFeeData.unitPrice || 0
                                        )
                                    }}
                                </td>
                                <td>
                                    {{
                                        formatPrice(colorMixFeeData.total || 0)
                                    }}
                                </td>
                            </tr>

                            <!-- Services with rollup_fee_id = 13 (Custom Color Fee services) -->
                            <template
                                v-for="(
                                    service, index
                                ) in getColorMixFeeServices()"
                            >
                                <!-- Only show services with total > 0 -->
                                <template v-if="getServiceTotal(service) > 0">
                                    <!-- Services with show_service = false (grayed out, but show price) -->
                                    <tr
                                        v-if="!service.show_service"
                                        :key="
                                            'colormix-service-incl-' +
                                            service.id +
                                            '-' +
                                            index
                                        "
                                        class="disable_data"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td v-if="isPerItemService(service)">
                                            {{ getServiceQuantity(service) }}
                                        </td>
                                        <td v-else>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Services with show_service = true (normal display) -->
                                    <tr
                                        v-else
                                        :key="
                                            'colormix-service-' +
                                            service.id +
                                            '-' +
                                            index
                                        "
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>
                                </template>
                            </template>

                            <tr class="total-row">
                                <td :colspan="isBaseFeeBreakdown ? 4 : 3">
                                    <strong>Total</strong>
                                </td>
                                <td>
                                    <strong>{{
                                        formatPrice(
                                            getColorMixFeeTotalWithServices()
                                        )
                                    }}</strong>
                                </td>
                            </tr>
                        </template>

                        <!-- Services (Base Fee only - exclude rollup_fee_id 12, 13, 2) -->
                        <template v-for="service in getBaseFeeServices()">
                            <!-- Services included (show_service = false) -->
                            <tr
                                v-if="!service.show_service"
                                :key="'incl-' + service.id"
                                class="disable_data"
                            >
                                <td>{{ getServiceName(service) }}</td>
                                <td v-if="isBaseFeeBreakdown">-</td>
                                <td v-if="service.fee_structure === 'Per Item'">
                                    {{ quantity }}
                                </td>
                                <td v-else>1</td>
                                <td>INCL</td>
                                <td>INCL</td>
                            </tr>

                            <!-- Services shown as separate lines (show_service = true) -->
                            <template v-else>
                                <tr
                                    v-if="getServiceTotal(service) > 0"
                                    :key="'service-' + service.id"
                                >
                                    <td>{{ getServiceName(service) }}</td>
                                    <td v-if="isBaseFeeBreakdown">-</td>
                                    <td v-if="isPerItemService(service)">
                                        {{ quantity }}
                                    </td>
                                    <td v-else>1</td>
                                    <td v-if="isPerItemService(service)">
                                        {{
                                            formatPrice(
                                                service.retail_cost ||
                                                    service.net_cost ||
                                                    0
                                            )
                                        }}
                                    </td>
                                    <td v-else>
                                        {{
                                            formatPrice(
                                                service.retail_cost ||
                                                    service.net_cost ||
                                                    0
                                            )
                                        }}
                                    </td>
                                    <td v-if="isPerItemService(service)">
                                        {{
                                            formatPrice(
                                                (service.retail_cost ||
                                                    service.net_cost ||
                                                    0) * quantity
                                            )
                                        }}
                                    </td>
                                    <td v-else>
                                        {{
                                            formatPrice(
                                                service.retail_cost ||
                                                    service.net_cost ||
                                                    0
                                            )
                                        }}
                                    </td>
                                </tr>
                            </template>
                        </template>

                        <!-- Packaging, Kitting and Handling Breakdown -->
                        <template
                            v-if="
                                getPackagingServices() &&
                                getPackagingServices().length > 0
                            "
                        >
                            <!-- Services with rollup_fee_id = 5 (Packaging services) -->
                            <template v-for="service in getPackagingServices()">
                                <!-- Only show services with total > 0 -->
                                <template v-if="getServiceTotal(service) > 0">
                                    <!-- Services with show_service = false (grayed out, but show price) -->
                                    <tr
                                        v-if="!service.show_service"
                                        :key="
                                            'packaging-service-incl-' +
                                            service.id
                                        "
                                        class="disable_data"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td v-if="isPerItemService(service)">
                                            {{ getServiceQuantity(service) }}
                                        </td>
                                        <td v-else>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Services with show_service = true (normal display) -->
                                    <tr
                                        v-else
                                        :key="'packaging-service-' + service.id"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>
                                </template>
                            </template>

                            <!-- Total Row for Packaging, Kitting and Handling -->
                            <tr class="total-row">
                                <td :colspan="isBaseFeeBreakdown ? 4 : 3">
                                    <strong>Total</strong>
                                </td>
                                <td>
                                    <strong>{{
                                        formatPrice(getPackagingTotal())
                                    }}</strong>
                                </td>
                            </tr>
                        </template>

                        <!-- Shipping and Freight Breakdown -->
                        <template
                            v-if="
                                shippingMethodCharge > 0 ||
                                (getShippingServices() &&
                                    getShippingServices().length > 0)
                            "
                        >
                            <!-- Shipping Method Charge -->
                            <tr v-if="shippingMethodCharge > 0">
                                <td>Shipping Charge</td>
                                <td v-if="isBaseFeeBreakdown">-</td>
                                <td>-</td>
                                <td>-</td>
                                <td>{{ formatPrice(shippingMethodCharge) }}</td>
                            </tr>

                            <!-- Services with rollup_fee_id = 4 (Shipping services) -->
                            <template v-for="service in getShippingServices()">
                                <!-- Only show services with total > 0 -->
                                <template v-if="getServiceTotal(service) > 0">
                                    <!-- Services with show_service = false (grayed out, but show price) -->
                                    <tr
                                        v-if="!service.show_service"
                                        :key="
                                            'shipping-service-incl-' +
                                            service.id
                                        "
                                        class="disable_data"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td v-if="isPerItemService(service)">
                                            {{ getServiceQuantity(service) }}
                                        </td>
                                        <td v-else>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Services with show_service = true (normal display) -->
                                    <tr
                                        v-else
                                        :key="'shipping-service-' + service.id"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>
                                </template>
                            </template>

                            <!-- Total Row for Shipping and Freight -->
                            <tr class="total-row">
                                <td :colspan="isBaseFeeBreakdown ? 4 : 3">
                                    <strong>Total</strong>
                                </td>
                                <td>
                                    <strong>{{
                                        formatPrice(getShippingTotal())
                                    }}</strong>
                                </td>
                            </tr>
                        </template>

                        <!-- Sales Tax Breakdown -->
                        <template
                            v-if="
                                salesTax > 0 ||
                                (getSalesTaxServices() &&
                                    getSalesTaxServices().length > 0)
                            "
                        >
                            <!-- Sales Tax from order_data -->
                            <tr v-if="salesTax > 0">
                                <td>Sales Tax</td>
                                <td v-if="isBaseFeeBreakdown">-</td>
                                <td>-</td>
                                <td>-</td>
                                <td>{{ formatPrice(salesTax) }}</td>
                            </tr>

                            <!-- Services with rollup_fee_id = 3 (Sales Tax services) -->
                            <template v-for="service in getSalesTaxServices()">
                                <!-- Only show services with total > 0 -->
                                <template v-if="getServiceTotal(service) > 0">
                                    <!-- Services with show_service = false (grayed out, but show price) -->
                                    <tr
                                        v-if="!service.show_service"
                                        :key="
                                            'sales-tax-service-incl-' +
                                            service.id
                                        "
                                        class="disable_data"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td v-if="isPerItemService(service)">
                                            {{ getServiceQuantity(service) }}
                                        </td>
                                        <td v-else>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Services with show_service = true (normal display) -->
                                    <tr
                                        v-else
                                        :key="'sales-tax-service-' + service.id"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td v-if="isBaseFeeBreakdown">-</td>
                                        <td>
                                            {{
                                                getServiceQuantity(service) !==
                                                "-"
                                                    ? getServiceQuantity(
                                                          service
                                                      )
                                                    : 1
                                            }}
                                        </td>
                                        <td v-if="isPerItemService(service)">
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            {{
                                                formatPrice(
                                                    getServiceUnitPrice(service)
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getServiceTotal(service)
                                                )
                                            }}
                                        </td>
                                    </tr>
                                </template>
                            </template>

                            <!-- Total Row for Sales Tax -->
                            <tr class="total-row">
                                <td :colspan="isBaseFeeBreakdown ? 4 : 3">
                                    <strong>Total</strong>
                                </td>
                                <td>
                                    <strong>{{
                                        formatPrice(getSalesTaxTotal())
                                    }}</strong>
                                </td>
                            </tr>
                        </template>

                        <!-- Product Base Price (Total) - shown at end after services if color-size combinations exist -->
                        <tr
                            v-if="
                                colorSizeCombinations &&
                                colorSizeCombinations.length > 0 &&
                                (basePrice > 0 || getBaseUnitPrice() > 0)
                            "
                            class="product-base-price-row d-none"
                        >
                            <td>
                                <strong>{{
                                    basePriceLabel || "Product Base Price"
                                }}</strong>
                            </td>
                            <td v-if="isBaseFeeBreakdown">-</td>
                            <td>
                                <strong>{{ quantity }}</strong>
                            </td>
                            <td>
                                <strong>{{
                                    formatPrice(getBaseUnitPrice())
                                }}</strong>
                            </td>
                            <td>
                                <strong>{{
                                    formatPrice(getProductBasePriceTotal())
                                }}</strong>
                            </td>
                        </tr>

                        <!-- Total Row (hide if Run Charge Breakdown only tooltip, Color Mix Fee only tooltip, Setup Fee only tooltip, or Shipping only tooltip, as they have their own total rows) -->
                        <tr
                            v-if="
                                getTotalAmount() > 0 &&
                                !isRunChargeOnlyTooltip() &&
                                !isColorMixFeeOnlyTooltip() &&
                                !isSetupFeeOnlyTooltip() &&
                                !isShippingOnlyTooltip()
                            "
                            class="total-row"
                        >
                            <td :colspan="isBaseFeeBreakdown ? 4 : 3">
                                <strong>Total</strong>
                            </td>
                            <td>
                                <strong>{{
                                    formatPrice(getTotalAmount())
                                }}</strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { rollupFeeMixin } from "@/common/rollupFee.service";
import { ecommDisplayNameMixin } from "@/common/ecommDisplayName.service";

export default {
    name: "ToolTip",
    mixins: [rollupFeeMixin, ecommDisplayNameMixin],
    props: {
        label: {
            type: String,
            required: true
        },
        title: {
            type: String,
            default: "Breakdown"
        },
        basePrice: {
            type: Number,
            default: 0
        },
        basePriceLabel: {
            type: String,
            default: "Product Base Price"
        },
        quantity: {
            type: Number,
            default: 0
        },
        services: {
            type: Array,
            default: () => []
        },
        formatPrice: {
            type: Function,
            required: true
        },
        setupFeeDetails: {
            type: Object,
            default: null
        },
        baseUnitPrice: {
            type: Number,
            default: null
        },
        runChargeBreakdown: {
            type: Array,
            default: () => []
        },
        colorMixFeeData: {
            type: Object,
            default: null
        },
        colorSizeCombinations: {
            type: Array,
            default: () => []
        },
        decorationType: {
            type: Number,
            default: null
        },
        masterService: {
            type: Object,
            default: null
        },
        shippingMethodCharge: {
            type: Number,
            default: 0
        },
        salesTax: {
            type: Number,
            default: 0
        },
        rushFeeBreakdown: {
            type: Object,
            default: null
        },
        patchCostDetails: {
            type: Array,
            default: null
        },
        patchCostTotal: {
            type: Number,
            default: 0
        },
        discountConfig: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            tooltipVisible: false,
            tooltipId: null
        };
    },
    mounted() {
        // Generate unique ID for this tooltip
        this.tooltipId =
            "tooltip-" +
            Date.now() +
            "-" +
            Math.random().toString(36).substr(2, 9);

        // Listen for close events from other tooltips
        const closeHandler = (excludeId) => {
            if (this.tooltipId !== excludeId) {
                this.tooltipVisible = false;
            }
        };

        this.$root.$on("close-all-tooltips", closeHandler);

        // Store handler for cleanup
        this._closeHandler = closeHandler;

        // Close tooltip when clicking outside
        document.addEventListener("click", this.closeTooltipOnClickOutside);
    },
    beforeDestroy() {
        // Clean up event listener
        if (this._closeHandler) {
            this.$root.$off("close-all-tooltips", this._closeHandler);
        }
        // Remove click outside listener
        document.removeEventListener("click", this.closeTooltipOnClickOutside);
    },
    computed: {
        // Get dynamic label based on rollup fee ID mapping
        displayLabel() {
            // Map common fee labels to rollup fee IDs
            const feeLabelMap = {
                "Base Fee": 1,
                "Run Charge": 12,
                "Custom Color Fee": 13,
                "Setup Fee": 2,
                "Set Up Fees": 2,
                "Set Up Fee": 2,
                "Sales Tax": 3,
                "Shipping and Freight": 4,
                "Shipping And Freight": 4,
                "Packaging, Kitting and Handling": 5,
                "Packaging, Kitting And Handling": 5
            };

            // Check if label matches any known fee name
            const feeId = feeLabelMap[this.label];
            if (feeId) {
                const dynamicName = this.getRollupFeeDisplayName(feeId);
                return dynamicName || this.label;
            }

            // Return original label if no match found
            return this.label;
        },
        // Get dynamic title based on label
        displayTitle() {
            // If title contains "Breakdown", replace the fee name part
            if (this.title && this.title.includes("Breakdown")) {
                const feeLabelMap = {
                    "Base Fee": 1,
                    "Run Charge": 12,
                    "Custom Color Fee": 13,
                    "Setup Fee": 2,
                    "Set Up Fees": 2,
                    "Set Up Fee": 2,
                    "Sales Tax": 3,
                    "Shipping and Freight": 4,
                    "Shipping And Freight": 4,
                    "Packaging, Kitting and Handling": 5,
                    "Packaging, Kitting And Handling": 5
                };

                // Try to find matching fee name in title
                for (const [feeName, feeId] of Object.entries(feeLabelMap)) {
                    if (this.title.includes(feeName)) {
                        const dynamicName = this.getRollupFeeDisplayName(feeId);
                        if (dynamicName) {
                            return this.title.replace(feeName, dynamicName);
                        }
                    }
                }
            }
            return this.title;
        },
        setupFeeTotal() {
            let total = 0;

            if (this.setupFeeDetails) {
                // Use total from setupFeeDetails if available and > 0
                if (
                    this.setupFeeDetails.total &&
                    parseFloat(this.setupFeeDetails.total) > 0
                ) {
                    total = parseFloat(this.setupFeeDetails.total);
                } else if (
                    this.setupFeeDetails.locations &&
                    this.setupFeeDetails.locations.length > 0
                ) {
                    // Calculate from locations if total is not available
                    total = this.setupFeeDetails.locations.reduce(
                        (sum, location) => {
                            return sum + parseFloat(location.total || 0);
                        },
                        0
                    );
                }
            }

            // Add services with rollup_fee_id = 2 (Setup Fee services) - include all services regardless of show_service
            const setupFeeServices = this.getSetupFeeServices();
            setupFeeServices.forEach((service) => {
                total += this.getServiceTotal(service);
            });

            return parseFloat(total.toFixed(2));
        },
        rushFeeTotal() {
            let total = 0;

            if (this.rushFeeBreakdown && this.rushFeeBreakdown.services) {
                // Recalculate based on current quantity (don't use stored total)
                this.rushFeeBreakdown.services.forEach((service) => {
                    const servicePrice = parseFloat(service.retail_cost || 0);

                    if (servicePrice > 0) {
                        // Rush options: rush_package is Per Order (multiply by 1), others (rush1, rush3, rush5, rush7) are Per Item (multiply by quantity)
                        if (service.is_rush_option === true) {
                            // Check if it's rush_package or other rush options
                            if (service.service_id === "rush_package") {
                                // Rush Package: multiply by 1 (fixed price)
                                total += servicePrice * 1;
                            } else {
                                // Other rush options (rush1, rush3, rush5, rush7): multiply by quantity
                                total += servicePrice * this.quantity;
                            }
                        } else {
                            // For other rush fee services, check fee_structure
                            const isPerItem =
                                service.fee_structure === "Per Item" ||
                                service.fee_structure === 6 ||
                                service.fee_structure === "6";

                            if (isPerItem) {
                                // Per Item: multiply by current quantity
                                total += servicePrice * this.quantity;
                            } else {
                                // Per Order: use price as-is
                                total += servicePrice;
                            }
                        }
                    }
                });
            }

            return parseFloat(total.toFixed(2));
        },
        hasContent() {
            const hasSetupFee =
                this.setupFeeTotal > 0 ||
                (this.getSetupFeeServices() &&
                    this.getSetupFeeServices().length > 0);

            const hasRunChargeBreakdown =
                this.runChargeBreakdown && this.runChargeBreakdown.length > 0;
            const hasColorMixFee =
                (this.colorMixFeeData && this.colorMixFeeData !== null) ||
                (this.getColorMixFeeServices() &&
                    this.getColorMixFeeServices().length > 0);

            const hasRushFee =
                this.rushFeeTotal > 0 ||
                (this.rushFeeBreakdown &&
                    this.rushFeeBreakdown.services &&
                    this.rushFeeBreakdown.services.length > 0);

            // Check for rollup_fee_id 12, 13, 2, 4, and 5 services
            const hasRollup12Services = this.getRunChargeServices().length > 0;
            const hasRollup13Services =
                this.getColorMixFeeServices().length > 0;
            const hasRollup2Services = this.getSetupFeeServices().length > 0;
            const hasRollup4Services = this.getShippingServices().length > 0;
            const hasRollup5Services = this.getPackagingServices().length > 0;

            const hasShipping =
                this.shippingMethodCharge > 0 || hasRollup4Services;
            const hasPackaging = hasRollup5Services;
            const hasRollup3Services = this.getSalesTaxServices().length > 0;
            const hasSalesTax = this.salesTax > 0 || hasRollup3Services;

            const hasBaseFeeServices = this.getBaseFeeServices().length > 0;

            const hasPatchCost =
                this.patchCostDetails &&
                this.patchCostDetails.length > 0;

            return (
                this.basePrice > 0 ||
                hasBaseFeeServices ||
                hasSetupFee ||
                hasRunChargeBreakdown ||
                hasColorMixFee ||
                hasRushFee ||
                hasPatchCost ||
                hasRollup12Services ||
                hasRollup13Services ||
                hasRollup2Services ||
                hasRollup3Services ||
                hasRollup5Services ||
                hasShipping ||
                hasPackaging ||
                hasSalesTax
            );
        },
        // Check if this is Base Fee Breakdown (has basePrice or baseFeeServices, and NOT a Run Charge/Color Mix Fee/Setup Fee/Shipping only tooltip)
        isBaseFeeBreakdown() {
            // If this is a Run Charge only, Color Mix Fee only, Setup Fee only, or Shipping only tooltip, don't show Color column
            if (
                this.isRunChargeOnlyTooltip() ||
                this.isColorMixFeeOnlyTooltip() ||
                this.isSetupFeeOnlyTooltip() ||
                this.isShippingOnlyTooltip()
            ) {
                return false;
            }
            // Show Color column only for Base Fee breakdown (has basePrice or baseFeeServices or colorSizeCombinations)
            return (
                this.basePrice > 0 ||
                (this.getBaseFeeServices() &&
                    this.getBaseFeeServices().length > 0) ||
                (this.colorSizeCombinations &&
                    this.colorSizeCombinations.length > 0)
            );
        }
    },
    methods: {
        toggleTooltip() {
            const wasVisible = this.tooltipVisible;

            // If opening this tooltip, close all others first
            if (!wasVisible) {
                // Close all other tooltips immediately
                this.$root.$emit("close-all-tooltips", this.tooltipId);
                // Then open this one
                this.$nextTick(() => {
                    this.tooltipVisible = true;
                });
            } else {
                // Just toggle if already visible
                this.tooltipVisible = false;
            }

            // Emit event to parent
            this.$emit("tooltip-toggle", !wasVisible);
        },
        getBaseUnitPrice() {
            let baseUnitPrice = 0;

            // If color-size combinations exist, calculate unit price from them
            // Logic: Total amount (size1.cost * size1.quantity + size2.cost * size2.quantity + ...) / Total quantity (size1.quantity + size2.quantity + ...)
            if (
                this.colorSizeCombinations &&
                this.colorSizeCombinations.length > 0
            ) {
                let totalAmount = 0;
                let totalQuantity = 0;

                this.colorSizeCombinations.forEach((combination) => {
                    const cost = parseFloat(combination.cost || 0);
                    const qty = parseInt(combination.quantity || 0);
                    totalAmount += cost * qty;
                    totalQuantity += qty;
                });

                if (totalQuantity > 0) {
                    baseUnitPrice = parseFloat(
                        (totalAmount / totalQuantity).toFixed(2)
                    );
                }
            } else {
                // Use baseUnitPrice from tiers if provided, otherwise calculate from basePrice
                if (
                    this.baseUnitPrice !== null &&
                    this.baseUnitPrice !== undefined
                ) {
                    baseUnitPrice = parseFloat(this.baseUnitPrice);
                } else if (this.quantity > 0 && this.basePrice > 0) {
                    baseUnitPrice = parseFloat(this.basePrice / this.quantity);
                }
            }

            // Add included services unit price
            const includedServicesUnitPrice =
                this.getIncludedServicesUnitPrice();
            return parseFloat(
                (baseUnitPrice + includedServicesUnitPrice).toFixed(2)
            );
        },
        getServiceName(service) {
            return service.name || service.ecomm_display || "Service";
        },
        isPerItemService(service) {
            if (!service) return false;
            return (
                service.fee_structure === "Per Item" ||
                (service.rollup_service &&
                    service.rollup_service.fee_structure_name === "Per Item")
            );
        },
        // Get Rush Fee Service Quantity (recalculate based on current quantity)
        getRushFeeServiceQuantity(service) {
            if (!service) return 1;

            // Rush options: rush_package is Per Order (quantity = 1), others (rush1, rush3, rush5, rush7) are Per Item (quantity = current quantity)
            if (service.is_rush_option === true) {
                // Check if it's rush_package or other rush options
                if (service.service_id === "rush_package") {
                    // Rush Package: quantity = 1
                    return 1;
                } else {
                    // Other rush options (rush1, rush3, rush5, rush7): quantity = current quantity
                    return this.quantity || 1;
                }
            }

            // For other rush fee services, check fee_structure
            const isPerItem =
                service.fee_structure === "Per Item" ||
                service.fee_structure === 6 ||
                service.fee_structure === "6";

            if (isPerItem) {
                // Per Item: return current quantity
                return this.quantity || 1;
            } else {
                // Per Order: return 1
                return 1;
            }
        },

        // Get Rush Fee Service Total (recalculate based on current quantity)
        getRushFeeServiceTotal(service) {
            if (!service) return 0;

            const servicePrice = parseFloat(service.retail_cost || 0);
            if (servicePrice <= 0) return 0;

            // Rush options: rush_package is Per Order (multiply by 1), others (rush1, rush3, rush5, rush7) are Per Item (multiply by quantity)
            if (service.is_rush_option === true) {
                // Check if it's rush_package or other rush options
                if (service.service_id === "rush_package") {
                    // Rush Package: multiply by 1 (fixed price)
                    return parseFloat((servicePrice * 1).toFixed(2));
                } else {
                    // Other rush options (rush1, rush3, rush5, rush7): multiply by quantity
                    return parseFloat((servicePrice * (this.quantity || 1)).toFixed(2));
                }
            }

            // For other rush fee services, check fee_structure
            const isPerItem =
                service.fee_structure === "Per Item" ||
                service.fee_structure === 6 ||
                service.fee_structure === "6";

            if (isPerItem) {
                // Per Item: multiply by current quantity
                return parseFloat(
                    (servicePrice * (this.quantity || 1)).toFixed(2)
                );
            } else {
                // Per Order: use price as-is
                return parseFloat(servicePrice.toFixed(2));
            }
        },

        // Get rush service name with delivery date (calculate at runtime, same as ProductDetailsV3New.vue)
        getRushServiceNameWithDate(service) {
            if (!service) {
                return "Rush Fee Service";
            }

            const serviceName = service.service_name || "Rush Fee Service";

            // Only show date for rush option services (is_rush_option === true)
            if (!service.is_rush_option) {
                return serviceName;
            }

            // Calculate date at runtime based on service_id (same as Cart.vue)
            if (service.service_id) {
                let daysToAdd = 0;
                switch (service.service_id) {
                    case "rush1":
                    case "rush_package":
                        daysToAdd = 1;
                        break;
                    case "rush3":
                        daysToAdd = 3;
                        break;
                    case "rush5":
                        daysToAdd = 5;
                        break;
                    case "rush7":
                        daysToAdd = 7;
                        break;
                    default:
                        daysToAdd = 0;
                }

                if (daysToAdd > 0) {
                    // Get holidays from parent Cart component if available
                    let holidays = [];
                    if (typeof window !== "undefined" && window.CartV3Component) {
                        holidays = window.CartV3Component.holidays || [];
                    }

                    console.log("[ToolTip] getRushServiceNameWithDate calculation:", {
                        service_id: service.service_id,
                        serviceName: serviceName,
                        daysToAdd: daysToAdd,
                        holidaysCount: holidays.length,
                        holidays: holidays.slice(0, 5) // Show first 5 holidays
                    });

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    console.log("[ToolTip] Today date:", today.toISOString().split("T")[0]);
                    
                    const estimatedDate = this.calculateEstimatedDateWithHolidays(
                        today,
                        daysToAdd,
                        holidays
                    );

                    console.log("[ToolTip] Estimated date (Date object):", {
                        date: estimatedDate,
                        isoString: estimatedDate.toISOString(),
                        dateString: estimatedDate.toISOString().split("T")[0],
                        getDate: estimatedDate.getDate(),
                        getMonth: estimatedDate.getMonth(),
                        getFullYear: estimatedDate.getFullYear()
                    });

                    // Format date as MM/DD/YYYY - use local timezone methods to avoid UTC conversion issues
                    // Create a new Date object in local timezone to ensure correct day/month/year
                    const localDate = new Date(estimatedDate.getFullYear(), estimatedDate.getMonth(), estimatedDate.getDate());
                    const mm = String(localDate.getMonth() + 1).padStart(2, "0");
                    const dd = String(localDate.getDate()).padStart(2, "0");
                    const yyyy = localDate.getFullYear();
                    const formattedDate = `${mm}/${dd}/${yyyy}`;
                    
                    console.log("[ToolTip] Formatted date:", {
                        mm: mm,
                        dd: dd,
                        yyyy: yyyy,
                        formattedDate: formattedDate,
                        finalString: `${serviceName} (${formattedDate})`
                    });
                    
                    return `${serviceName} (${formattedDate})`;
                }
            }

            // Return service name without date if no date available
            return serviceName;
        },

        // Calculate estimated date with holidays excluded (same as ProductDetailsV3New.vue)
        // calculateEstimatedDateWithHolidays(baseDate, daysToAdd, holidays = []) {
        //     let currentDate = new Date(baseDate);
        //     currentDate.setHours(0, 0, 0, 0);
        //     let daysAdded = 0;

        //     console.log("[ToolTip] calculateEstimatedDateWithHolidays called:", {
        //         baseDate: baseDate.toISOString().split("T")[0],
        //         daysToAdd: daysToAdd,
        //         holidaysCount: holidays.length
        //     });

        //     // Convert holidays to Date objects for comparison
        //     const holidayDates = holidays.map((h) => {
        //         const d = new Date(h);
        //         d.setHours(0, 0, 0, 0);
        //         return d;
        //     });

        //     console.log("[ToolTip] Holiday dates converted:", holidayDates.map(d => d.toISOString().split("T")[0]).slice(0, 10));

        //     // Add days one by one, skipping holidays
        //     while (daysAdded < daysToAdd) {
        //         currentDate.setDate(currentDate.getDate() + 1);

        //         // Check if current date is a holiday
        //         const currentDateOnly = new Date(currentDate);
        //         currentDateOnly.setHours(0, 0, 0, 0);

        //         const isHoliday = holidayDates.some((h) => {
        //             return h.getTime() === currentDateOnly.getTime();
        //         });

        //         if (isHoliday) {
        //             console.log("[ToolTip] Skipping holiday:", currentDateOnly.toISOString().split("T")[0]);
        //         }

        //         // Only count non-holiday days
        //         if (!isHoliday) {
        //             daysAdded++;
        //             console.log("[ToolTip] Day added:", {
        //                 date: currentDateOnly.toISOString().split("T")[0],
        //                 daysAdded: daysAdded,
        //                 daysToAdd: daysToAdd
        //             });
        //         }
        //     }

        //     // Ensure final date is not a holiday (add extra days if needed)
        //     let finalDateOnly = new Date(currentDate);
        //     finalDateOnly.setHours(0, 0, 0, 0);
            
        //     console.log("[ToolTip] Final date check (before holiday check):", finalDateOnly.toISOString().split("T")[0]);
            
        //     while (
        //         holidayDates.some(
        //             (h) => h.getTime() === finalDateOnly.getTime()
        //         )
        //     ) {
        //         console.log("[ToolTip] Final date is holiday, moving forward:", finalDateOnly.toISOString().split("T")[0]);
        //         currentDate.setDate(currentDate.getDate() + 1);
        //         finalDateOnly = new Date(currentDate);
        //         finalDateOnly.setHours(0, 0, 0, 0);
        //     }

        //     console.log("[ToolTip] Final calculated date:", currentDate.toISOString().split("T")[0]);
        //     return currentDate;
        // },
        calculateEstimatedDateWithHolidays(baseDate, daysToAdd, holidays = []) {
            try {
                // Validate inputs
                if (!baseDate || !(baseDate instanceof Date) || isNaN(baseDate.getTime())) {
                    // Fallback: return baseDate + daysToAdd if invalid
                    var fallbackDate = new Date();
                    fallbackDate.setDate(fallbackDate.getDate() + (daysToAdd || 0));
                    return fallbackDate;
                }

                if (!daysToAdd || daysToAdd < 0) {
                    return new Date(baseDate);
                }

                if (!Array.isArray(holidays)) {
                    holidays = [];
                }

                // Helper: get YYYY-MM-DD date string in New York timezone
                // This makes holiday comparison universal across all timezones
                var toNYDateString = function(date) {
                    try {
                        if (typeof Intl !== "undefined" && Intl.DateTimeFormat) {
                            return new Intl.DateTimeFormat("en-CA", {
                                timeZone: "America/New_York",
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit"
                            }).format(date);
                        }
                    } catch (e) {
                        // Fallback if Intl.DateTimeFormat fails
                    }
                    // Fallback: use UTC date string (YYYY-MM-DD)
                    var year = date.getUTCFullYear();
                    var month = String(date.getUTCMonth() + 1).padStart(2, "0");
                    var day = String(date.getUTCDate()).padStart(2, "0");
                    return year + "-" + month + "-" + day;
                };

                // Build a Set of NY holiday date strings
                // API returns "YYYY-MM-DD" strings — treat them as NY calendar dates directly
                // (no Date parsing needed, avoids UTC-shift problem entirely)
                var holidaySet = new Set();
                try {
                    holidays.forEach(function(h) {
                        if (typeof h === "string" && /^\d{4}-\d{2}-\d{2}$/.test(h)) {
                            holidaySet.add(h); // Already a NY calendar date string, use as-is
                        } else if (h) {
                            try {
                                var dateStr = toNYDateString(new Date(h));
                                if (dateStr) {
                                    holidaySet.add(dateStr);
                                }
                            } catch (e) {
                                // Skip invalid holiday dates
                            }
                        }
                    });
                } catch (e) {
                    // If holiday processing fails, continue without holidays
                    holidaySet = new Set();
                }

                // Start from base date
                var currentDate = new Date(baseDate);
                var daysAdded = 0;
                var maxIterations = daysToAdd * 2 + 10; // Safety limit to prevent infinite loops
                var iterations = 0;

                // Add days one by one, skipping holidays (compared in NY timezone)
                while (daysAdded < daysToAdd && iterations < maxIterations) {
                    iterations++;
                    currentDate.setDate(currentDate.getDate() + 1);
                    try {
                        if (!holidaySet.has(toNYDateString(currentDate))) {
                            daysAdded++;
                        }
                    } catch (e) {
                        // If date string conversion fails, count the day anyway
                        daysAdded++;
                    }
                }

                // Ensure final date is not a holiday (push forward if needed)
                iterations = 0;
                while (iterations < 10) {
                    iterations++;
                    try {
                        if (!holidaySet.has(toNYDateString(currentDate))) {
                            break;
                        }
                    } catch (e) {
                        // If date string conversion fails, assume it's not a holiday
                        break;
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }

                return currentDate;
            } catch (error) {
                // If anything goes wrong, return a safe fallback date
                console.error("[ToolTip] calculateEstimatedDateWithHolidays error:", error);
                var fallbackDate = new Date(baseDate);
                fallbackDate.setDate(fallbackDate.getDate() + (daysToAdd || 0));
                return fallbackDate;
            }
        },
        getServiceTotal(service) {
            // If service has pre-calculated total (for combined services from grouped items), use it
            if (
                service._calculatedTotal !== undefined &&
                service._calculatedTotal !== null
            ) {
                return parseFloat(service._calculatedTotal);
            }
            const servicePrice = service.retail_cost || service.net_cost || 0;
            if (this.isPerItemService(service)) {
                return parseFloat(servicePrice) * (this.quantity || 0);
            } else {
                return parseFloat(servicePrice);
            }
        },
        getServiceQuantity(service) {
            // If service has pre-calculated quantity (for combined services from grouped items), use it
            if (service._quantity !== undefined && service._quantity !== null) {
                return service._quantity;
            }
            if (this.isPerItemService(service)) {
                return this.quantity || 0;
            } else {
                // For non-Per Item, return 1 as default (but _quantity should be set from CartSummary)
                return 1;
            }
        },
        // Get Custom Color Fee label based on decoration type and service name
        getColorMixFeeLabel() {
            // If decoration_type is provided as prop
            if (
                this.decorationType !== null &&
                this.decorationType !== undefined
            ) {
                if (this.decorationType === 1) {
                    // Decoration type 1 = Screen Print = Custom Color Ink Fee
                    return "Custom Color Ink Fee";
                } else if (this.decorationType === 2) {
                    // Decoration type 2 = Embroidery = Custom Color Thread Fee
                    return "Custom Color Thread Fee";
                } else if (this.decorationType === 0) {
                    // Decoration type 0 = PCNA - check service name from services or colorMixFeeData
                    // First check service name from services (prioritize this over colorMixFeeData.label)
                    const colorMixFeeServices = this.getColorMixFeeServices();
                    if (colorMixFeeServices && colorMixFeeServices.length > 0) {
                        // Try to get service name from first service
                        const firstService = colorMixFeeServices[0];
                        const rollupService = firstService.rollup_service || {};
                        const masterService =
                            rollupService.master_service ||
                            firstService.master_service ||
                            {};

                        // Check multiple possible locations for service name
                        const serviceName = (
                            firstService.name ||
                            firstService.service_name ||
                            firstService.ecomm_display ||
                            masterService.name ||
                            masterService.service_name ||
                            rollupService.name ||
                            rollupService.service_name ||
                            ""
                        ).toLowerCase();

                        const isEmbroidery =
                            serviceName.includes("embroidery") ||
                            serviceName.includes("emb");
                        const isPrint = serviceName.includes("print");

                        if (isEmbroidery) {
                            return "Custom Color Thread Fee";
                        } else if (isPrint) {
                            return "Custom Color Ink Fee";
                        }
                    }

                    // If service name check didn't work, check colorMixFeeData label but process it
                    if (this.colorMixFeeData && this.colorMixFeeData.label) {
                        const label = this.colorMixFeeData.label.toLowerCase();
                        if (
                            label.includes("thread") ||
                            label.includes("embroidery") ||
                            label.includes("emb")
                        ) {
                            return "Custom Color Thread Fee";
                        } else if (
                            label.includes("ink") ||
                            label.includes("print")
                        ) {
                            return "Custom Color Ink Fee";
                        }
                        // If label contains old naming, still process it
                        if (label.includes("color mix fee")) {
                            return "Custom Color Fee";
                        }
                        return this.colorMixFeeData.label;
                    }
                }
            }

            // Fallback: try to determine from services first (before colorMixFeeData.label)
            const colorMixFeeServices = this.getColorMixFeeServices();
            if (colorMixFeeServices && colorMixFeeServices.length > 0) {
                const firstService = colorMixFeeServices[0];
                const rollupService = firstService.rollup_service || {};
                const masterService =
                    rollupService.master_service ||
                    firstService.master_service ||
                    {};

                // Check multiple possible locations for service name
                const serviceName = (
                    firstService.name ||
                    firstService.service_name ||
                    firstService.ecomm_display ||
                    masterService.name ||
                    masterService.service_name ||
                    rollupService.name ||
                    rollupService.service_name ||
                    ""
                ).toLowerCase();

                const isEmbroidery =
                    serviceName.includes("embroidery") ||
                    serviceName.includes("emb");
                const isPrint = serviceName.includes("print");

                if (isEmbroidery) {
                    return "Custom Color Thread Fee";
                } else if (isPrint) {
                    return "Custom Color Ink Fee";
                }
            }

            // Fallback: try to determine from colorMixFeeData label (process it)
            if (this.colorMixFeeData && this.colorMixFeeData.label) {
                const label = this.colorMixFeeData.label.toLowerCase();
                if (
                    label.includes("thread") ||
                    label.includes("embroidery") ||
                    label.includes("emb")
                ) {
                    return "Custom Color Thread Fee";
                } else if (label.includes("ink") || label.includes("print")) {
                    return "Custom Color Ink Fee";
                }
                // If label contains old naming, convert it
                if (label.includes("color thread mix fee")) {
                    return "Custom Color Thread Fee";
                } else if (label.includes("color ink mix fee")) {
                    return "Custom Color Ink Fee";
                } else if (label.includes("color mix fee")) {
                    return "Custom Color Fee";
                }
                return this.colorMixFeeData.label;
            }

            // Default fallback
            return "Custom Color Fee";
        },
        getServiceUnitPrice(service) {
            return parseFloat(service.retail_cost || service.net_cost || 0);
        },
        // Get service name with ecomm display name (clean logic, no fallbacks)
        getServiceName(service) {
            if (!service) return null;
            
            // Priority 1: Check ecomm_display directly
            if (service.ecomm_display) {
                return service.ecomm_display;
            }
            
            // Priority 2: Check master_service.ecomm_display
            if (service.master_service && service.master_service.ecomm_display) {
                return service.master_service.ecomm_display;
            }
            
            // Priority 3: Try master_service_id for store lookup (dynamic)
            const serviceId = (service.master_service && service.master_service.id) ||
                             service.master_service_id;
            if (serviceId) {
                const ecommDisplayName = this.getEcommDisplayName(serviceId);
                if (ecommDisplayName) {
                    return ecommDisplayName;
                }
            }
            
            // Priority 4: Try getOtherServiceEcommDisplayName (for other services)
            const otherServiceEcommDisplayName = this.getOtherServiceEcommDisplayName(service);
            if (otherServiceEcommDisplayName) {
                return otherServiceEcommDisplayName;
            }
            
            return null;
        },
        getRunChargeBreakdownTotal() {
            if (
                !this.runChargeBreakdown ||
                this.runChargeBreakdown.length === 0
            ) {
                return 0;
            }

            let total = 0;

            // Handle location-wise structure (new format)
            if (
                this.runChargeBreakdown[0] &&
                this.runChargeBreakdown[0].location &&
                this.runChargeBreakdown[0].items
            ) {
                this.runChargeBreakdown.forEach((location) => {
                    if (location.items && location.items.length > 0) {
                        location.items.forEach((row) => {
                            // Skip INCL and "-" rows
                            if (
                                row.isIncluded ||
                                row.total === "INCL" ||
                                row.total === "-" ||
                                row.amt === "INCL" ||
                                row.amt === "-"
                            ) {
                                return;
                            }
                            total += parseFloat(row.total || 0);
                        });
                    }
                });
            } else {
                // Handle flat array structure (old format - backward compatibility)
                this.runChargeBreakdown.forEach((row) => {
                    // Skip INCL and "-" rows
                    if (
                        row.isIncluded ||
                        row.total === "INCL" ||
                        row.total === "-" ||
                        row.amt === "INCL" ||
                        row.amt === "-"
                    ) {
                        return;
                    }
                    total += parseFloat(row.total || 0);
                });
            }

            // Add services with rollup_fee_id = 12 (Run Charge services) - include all services regardless of show_service
            const runChargeServices = this.getRunChargeServices();
            runChargeServices.forEach((service) => {
                total += this.getServiceTotal(service);
            });

            return parseFloat(total.toFixed(2));
        },
        // Check if this is a Run Charge only tooltip
        isRunChargeOnlyTooltip() {
            const hasRunCharge =
                (this.runChargeBreakdown &&
                    this.runChargeBreakdown.length > 0) ||
                (this.getRunChargeServices() &&
                    this.getRunChargeServices().length > 0);
            return (
                hasRunCharge &&
                this.basePrice === 0 &&
                (!this.services || this.getBaseFeeServices().length === 0) &&
                (!this.setupFeeDetails ||
                    !this.setupFeeDetails.locations ||
                    this.setupFeeDetails.locations.length === 0) &&
                (!this.getSetupFeeServices() ||
                    this.getSetupFeeServices().length === 0) &&
                (!this.colorMixFeeData || !this.colorMixFeeData.total) &&
                (!this.getColorMixFeeServices() ||
                    this.getColorMixFeeServices().length === 0)
            );
        },
        // Check if this is a Custom Color Fee only tooltip
        isColorMixFeeOnlyTooltip() {
            const hasColorMixFee =
                (this.colorMixFeeData && this.colorMixFeeData.total > 0) ||
                (this.getColorMixFeeServices() &&
                    this.getColorMixFeeServices().length > 0);
            return (
                hasColorMixFee &&
                this.basePrice === 0 &&
                (!this.services || this.getBaseFeeServices().length === 0) &&
                (!this.setupFeeDetails ||
                    !this.setupFeeDetails.locations ||
                    this.setupFeeDetails.locations.length === 0) &&
                (!this.getSetupFeeServices() ||
                    this.getSetupFeeServices().length === 0) &&
                (!this.runChargeBreakdown ||
                    this.runChargeBreakdown.length === 0) &&
                (!this.getRunChargeServices() ||
                    this.getRunChargeServices().length === 0)
            );
        },
        // Check if this is a Setup Fee only tooltip
        isSetupFeeOnlyTooltip() {
            const hasSetupFee =
                (this.setupFeeDetails && this.setupFeeTotal > 0) ||
                (this.getSetupFeeServices() &&
                    this.getSetupFeeServices().length > 0);
            return (
                hasSetupFee &&
                this.basePrice === 0 &&
                (!this.services || this.getBaseFeeServices().length === 0) &&
                (!this.runChargeBreakdown ||
                    this.runChargeBreakdown.length === 0) &&
                (!this.getRunChargeServices() ||
                    this.getRunChargeServices().length === 0) &&
                (!this.colorMixFeeData || !this.colorMixFeeData.total) &&
                (!this.getColorMixFeeServices() ||
                    this.getColorMixFeeServices().length === 0)
            );
        },
        // Check if this is a Shipping only tooltip
        isShippingOnlyTooltip() {
            const hasShipping =
                this.shippingMethodCharge > 0 ||
                (this.getShippingServices() &&
                    this.getShippingServices().length > 0);
            return (
                hasShipping &&
                this.basePrice === 0 &&
                (!this.services || this.getBaseFeeServices().length === 0) &&
                (!this.setupFeeDetails ||
                    !this.setupFeeDetails.locations ||
                    this.setupFeeDetails.locations.length === 0) &&
                (!this.getSetupFeeServices() ||
                    this.getSetupFeeServices().length === 0) &&
                (!this.runChargeBreakdown ||
                    this.runChargeBreakdown.length === 0) &&
                (!this.getRunChargeServices() ||
                    this.getRunChargeServices().length === 0) &&
                (!this.colorMixFeeData || !this.colorMixFeeData.total) &&
                (!this.getColorMixFeeServices() ||
                    this.getColorMixFeeServices().length === 0)
            );
        },
        // Get Run Charge services (rollup_fee_id = 12)
        getRunChargeServices() {
            if (!this.services || this.services.length === 0) return [];
            return this.services.filter((service) => {
                const rollupService = service.rollup_service || {};
                const serviceFeeId =
                    rollupService.rollup_fee_id !== undefined &&
                    rollupService.rollup_fee_id !== null
                        ? rollupService.rollup_fee_id
                        : service.rollup_fee_id !== undefined &&
                          service.rollup_fee_id !== null
                        ? service.rollup_fee_id
                        : null;
                return serviceFeeId === 12 || serviceFeeId === "12";
            });
        },
        // Get Custom Color Fee services (rollup_fee_id = 13)
        getColorMixFeeServices() {
            if (!this.services || this.services.length === 0) return [];
            return this.services.filter((service) => {
                const rollupService = service.rollup_service || {};
                const serviceFeeId =
                    rollupService.rollup_fee_id !== undefined &&
                    rollupService.rollup_fee_id !== null
                        ? rollupService.rollup_fee_id
                        : service.rollup_fee_id !== undefined &&
                          service.rollup_fee_id !== null
                        ? service.rollup_fee_id
                        : null;
                return serviceFeeId === 13 || serviceFeeId === "13";
            });
        },
        // Get Setup Fee services (rollup_fee_id = 2)
        getSetupFeeServices() {
            if (!this.services || this.services.length === 0) return [];
            return this.services.filter((service) => {
                const rollupService = service.rollup_service || {};
                const serviceFeeId =
                    rollupService.rollup_fee_id !== undefined &&
                    rollupService.rollup_fee_id !== null
                        ? rollupService.rollup_fee_id
                        : service.rollup_fee_id !== undefined &&
                          service.rollup_fee_id !== null
                        ? service.rollup_fee_id
                        : null;
                return serviceFeeId === 2 || serviceFeeId === "2";
            });
        },
        // Get Packaging services (rollup_fee_id = 5)
        getPackagingServices() {
            if (!this.services || this.services.length === 0) return [];
            return this.services.filter((service) => {
                const rollupService = service.rollup_service || {};
                const serviceFeeId =
                    rollupService.rollup_fee_id !== undefined &&
                    rollupService.rollup_fee_id !== null
                        ? rollupService.rollup_fee_id
                        : service.rollup_fee_id !== undefined &&
                          service.rollup_fee_id !== null
                        ? service.rollup_fee_id
                        : null;
                return serviceFeeId === 5 || serviceFeeId === "5";
            });
        },
        // Get Shipping services (rollup_fee_id = 4)
        getShippingServices() {
            if (!this.services || this.services.length === 0) return [];
            return this.services.filter((service) => {
                const rollupService = service.rollup_service || {};
                const serviceFeeId =
                    rollupService.rollup_fee_id !== undefined &&
                    rollupService.rollup_fee_id !== null
                        ? rollupService.rollup_fee_id
                        : service.rollup_fee_id !== undefined &&
                          service.rollup_fee_id !== null
                        ? service.rollup_fee_id
                        : null;
                return serviceFeeId === 4 || serviceFeeId === "4";
            });
        },
        // Get Sales Tax services (rollup_fee_id = 3)
        getSalesTaxServices() {
            if (!this.services || this.services.length === 0) return [];
            return this.services.filter((service) => {
                const rollupService = service.rollup_service || {};
                const serviceFeeId =
                    rollupService.rollup_fee_id !== undefined &&
                    rollupService.rollup_fee_id !== null
                        ? rollupService.rollup_fee_id
                        : service.rollup_fee_id !== undefined &&
                          service.rollup_fee_id !== null
                        ? service.rollup_fee_id
                        : null;
                return serviceFeeId === 3 || serviceFeeId === "3";
            });
        },
        // Get Sales Tax Total
        getSalesTaxTotal() {
            let total = parseFloat(this.salesTax || 0);

            const salesTaxServices = this.getSalesTaxServices();
            salesTaxServices.forEach((service) => {
                total += this.getServiceTotal(service);
            });

            return parseFloat(total.toFixed(2));
        },
        // Get Packaging Total
        getPackagingTotal() {
            let total = 0;

            const packagingServices = this.getPackagingServices();
            packagingServices.forEach((service) => {
                total += this.getServiceTotal(service);
            });

            return parseFloat(total.toFixed(2));
        },
        // Get Shipping Total
        getShippingTotal() {
            // Sum displayed row totals (rounded per-row) so the Total
            // matches exactly what the user sees in the table.
            let total = 0;

            const shippingServices = this.getShippingServices();
            shippingServices.forEach((service) => {
                // getServiceTotal already applies per-item/per-order logic.
                // We round each row to 2 decimals before summing to avoid
                // floating-point accumulation differences.
                const rowTotal = this.getServiceTotal(service);
                total += parseFloat(rowTotal.toFixed(2));
            });

            // Also include any non-service shippingMethodCharge if present
            if (this.shippingMethodCharge) {
                total += parseFloat(this.shippingMethodCharge.toFixed(2));
            }

            return parseFloat(total.toFixed(2));
        },
        // Get Base Fee services (rollup_fee_id = 1, or services not in 12, 13, 2, or 4)
        // Exclude services that are shown in Run Charge, Color Mix Fee, Setup Fee, or Shipping breakdowns
        getBaseFeeServices() {
            if (!this.services || this.services.length === 0) return [];
            return this.services.filter((service) => {
                const rollupService = service.rollup_service || {};
                const serviceFeeId =
                    rollupService.rollup_fee_id !== undefined &&
                    rollupService.rollup_fee_id !== null
                        ? rollupService.rollup_fee_id
                        : service.rollup_fee_id !== undefined &&
                          service.rollup_fee_id !== null
                        ? service.rollup_fee_id
                        : null;
                // Exclude rollup_fee_id 12 (Run Charge), 13 (Color Mix Fee), 2 (Setup Fee), and 4 (Shipping)
                // Include only rollup_fee_id = 1 or services without rollup_fee_id (base fee services)
                if (
                    serviceFeeId === 12 ||
                    serviceFeeId === "12" ||
                    serviceFeeId === 13 ||
                    serviceFeeId === "13" ||
                    serviceFeeId === 2 ||
                    serviceFeeId === "2" ||
                    serviceFeeId === 4 ||
                    serviceFeeId === "4"
                ) {
                    return false;
                }
                return (
                    serviceFeeId === 1 ||
                    serviceFeeId === "1" ||
                    serviceFeeId === null ||
                    serviceFeeId === undefined
                );
            });
        },
        // Get Custom Color Fee total including services
        getColorMixFeeTotalWithServices() {
            let total = 0;
            if (
                this.colorMixFeeData &&
                parseFloat(this.colorMixFeeData.total || 0) > 0
            ) {
                total += parseFloat(this.colorMixFeeData.total);
            }
            const colorMixFeeServices = this.getColorMixFeeServices();
            colorMixFeeServices.forEach((service) => {
                total += this.getServiceTotal(service);
            });
            return parseFloat(total.toFixed(2));
        },
        getColorValue(combination) {
            if (!combination) return "#cccccc";
            // Try different possible property names
            let colorValue =
                combination.color_value ||
                combination.hex ||
                combination.hash ||
                "";
            if (combination.color) {
                colorValue =
                    combination.color.color_value ||
                    combination.color.hex ||
                    combination.color.hash ||
                    colorValue;
            }
            // Ensure # prefix
            if (colorValue && !colorValue.startsWith("#")) {
                colorValue = "#" + colorValue;
            }
            return colorValue || "#cccccc";
        },
        getColorName(combination) {
            if (!combination) return "Color";
            const color = combination.color;
            return (
                combination.color_name ||
                (color && color.name) ||
                (color && color.color_name) ||
                "Color"
            );
        },
        // Get unit price for included services (show_service = false)
        // Only include base fee services (exclude rollup_fee_id 12, 13, 2)
        getIncludedServicesUnitPrice() {
            if (!this.services || this.services.length === 0) return 0;

            let includedServicesUnitPrice = 0;
            const baseFeeServices = this.getBaseFeeServices();
            baseFeeServices.forEach((service) => {
                if (!service.show_service) {
                    // Get unit price directly (retail_cost or net_cost), don't divide by quantity
                    const serviceUnitPrice = parseFloat(
                        service.retail_cost || service.net_cost || 0
                    );
                    includedServicesUnitPrice += serviceUnitPrice;
                }
            });

            return parseFloat(includedServicesUnitPrice.toFixed(4));
        },
        getColorSizeUnitPrice(combination) {
            if (!combination) return 0;
            let baseUnitPrice = 0;
            let needsDiscount = false; // Track if discount needs to be applied

            // Use the actual unit price from the combination (from childItem.unit_price)
            // Each color-size combination may have different unit price based on tier pricing
            if (combination.cost !== undefined) {
                // combination.cost already has product discount applied in getColorSizeCombinations
                baseUnitPrice = parseFloat(combination.cost) || 0;
                needsDiscount = false; // Discount already applied
            } else if (combination.total_cost && combination.quantity) {
                // Fallback: calculate from total_cost / quantity
                baseUnitPrice =
                    parseFloat(combination.total_cost) /
                        parseInt(combination.quantity) || 0;
                needsDiscount = true; // Need to apply discount for fallback
            } else {
                // Final fallback: use base unit price
                baseUnitPrice = this.getBaseUnitPrice();
                needsDiscount = true; // Need to apply discount for fallback
            }

            // Add included services unit price only if combination.cost doesn't already include it
            // CartSummary.vue now includes included services in combination.cost for decoration type 1 and 2
            // So we only add it if the combination doesn't have a flag indicating it's already included
            // For decoration type 1 and 2, if cost is provided, assume it already includes included services
            // For other types or if cost is not provided, add included services
            const shouldAddIncludedServices = !(
                combination._includesIncludedServices === true
            );
            const includedServicesUnitPrice = shouldAddIncludedServices
                ? this.getIncludedServicesUnitPrice()
                : 0;
            let finalUnitPrice = parseFloat(
                (baseUnitPrice + includedServicesUnitPrice).toFixed(2)
            );

            // Apply discount only if needed (for fallback cases)
            // If combination.cost is provided, discount is already applied in getColorSizeCombinations
            if (needsDiscount) {
                return this.applyDiscountToPrice(finalUnitPrice);
            }
            return finalUnitPrice;
        },
        // Apply discount to price if discountConfig is active
        applyDiscountToPrice(originalPrice) {
            if (!originalPrice || originalPrice <= 0) {
                return originalPrice;
            }

            // Just check if discount config exists and has required fields
            if (
                !this.discountConfig ||
                !this.discountConfig.discount_type ||
                !this.discountConfig.discount_value
            ) {
                return originalPrice;
            }

            const discountType = this.discountConfig.discount_type;
            const discountValue = parseFloat(
                this.discountConfig.discount_value || 0
            );

            if (discountType === "Percent") {
                return parseFloat(
                    (originalPrice * (1 - discountValue / 100)).toFixed(2)
                );
            } else {
                // Fixed amount discount
                return parseFloat(
                    Math.max(0, originalPrice - discountValue).toFixed(2)
                );
            }
        },
        getColorSizeTotalPrice(combination) {
            if (!combination || !combination.quantity) return 0;
            const unitPrice = this.getColorSizeUnitPrice(combination);
            const qty = parseInt(combination.quantity) || 0;
            return parseFloat((unitPrice * qty).toFixed(2));
        },
        getProductBasePriceTotal() {
            // Calculate total from color-size combinations if available
            if (
                this.colorSizeCombinations &&
                this.colorSizeCombinations.length > 0
            ) {
                let total = 0;
                this.colorSizeCombinations.forEach((combination) => {
                    total += this.getColorSizeTotalPrice(combination);
                });
                return parseFloat(total.toFixed(2));
            }
            // Fallback to base price calculation
            const baseUnitPrice = this.getBaseUnitPrice();
            const discountedBaseUnitPrice =
                this.applyDiscountToPrice(baseUnitPrice);
            return parseFloat(
                (discountedBaseUnitPrice * (this.quantity || 0)).toFixed(2)
            );
        },
        getTotalAmount() {
            let total = 0;

            // If this is a Custom Color Fee only tooltip, return just the custom color fee total
            if (
                this.colorMixFeeData &&
                this.colorMixFeeData.total > 0 &&
                this.basePrice === 0 &&
                (!this.services || this.services.length === 0) &&
                (!this.setupFeeDetails ||
                    !this.setupFeeDetails.locations ||
                    this.setupFeeDetails.locations.length === 0) &&
                (!this.runChargeBreakdown ||
                    this.runChargeBreakdown.length === 0)
            ) {
                return parseFloat(this.colorMixFeeData.total || 0);
            }

            // If color-size combinations exist, calculate from them (already includes included services)
            if (
                this.colorSizeCombinations &&
                this.colorSizeCombinations.length > 0
            ) {
                this.colorSizeCombinations.forEach((combination) => {
                    total += this.getColorSizeTotalPrice(combination);
                });
            } else {
                // Calculate base price from tier-based unit price × quantity (already includes included services)
                const baseUnitPrice = this.getBaseUnitPrice();
                const discountedBaseUnitPrice =
                    this.applyDiscountToPrice(baseUnitPrice);
                const baseTotal =
                    discountedBaseUnitPrice * (this.quantity || 0);
                total += baseTotal;
            }

            // Add setup fee total
            total += this.setupFeeTotal || 0;

            // Add run charge breakdown total (for decoration type 0, 1, and 2)
            if (this.runChargeBreakdown && this.runChargeBreakdown.length > 0) {
                total += this.getRunChargeBreakdownTotal();
            }

            // Add custom color fee total (for decoration type 0) - only if not a standalone tooltip
            // Use getColorMixFeeTotalWithServices to include rollup_fee_id = 13 services
            if (
                this.colorMixFeeData &&
                (this.basePrice > 0 ||
                    (this.services && this.services.length > 0) ||
                    (this.setupFeeDetails &&
                        this.setupFeeDetails.locations &&
                        this.setupFeeDetails.locations.length > 0) ||
                    (this.runChargeBreakdown &&
                        this.runChargeBreakdown.length > 0))
            ) {
                total += this.getColorMixFeeTotalWithServices();
            } else if (this.colorMixFeeData && this.colorMixFeeData.total > 0) {
                // Standalone tooltip - use getColorMixFeeTotalWithServices
                total += this.getColorMixFeeTotalWithServices();
            }

            // Add only base fee services with show_service = true (exclude rollup_fee_id 12, 13, 2)
            // Services with rollup_fee_id 12, 13, and 2 are already included in their respective breakdown totals
            const baseFeeServices = this.getBaseFeeServices();
            if (baseFeeServices && baseFeeServices.length > 0) {
                baseFeeServices.forEach((service) => {
                    if (service.show_service) {
                        const servicePrice =
                            service.retail_cost || service.net_cost || 0;
                        if (service.fee_structure === "Per Item") {
                            total +=
                                parseFloat(servicePrice) * (this.quantity || 0);
                        } else {
                            total += parseFloat(servicePrice);
                        }
                    }
                });
            }

            return parseFloat(total.toFixed(2));
        },
        closeTooltip() {
            this.tooltipVisible = false;
            this.$emit("tooltip-toggle", false);
        },
        closeTooltipOnClickOutside(event) {
            // Don't close if clicking on the tooltip wrapper or content
            if (!this.$el.contains(event.target)) {
                // Check if clicking on another tooltip - if so, let that tooltip handle it
                const clickedTooltip = event.target.closest(".tooltip-wrapper");
                if (!clickedTooltip) {
                    this.tooltipVisible = false;
                }
            }
        }
    }
};
</script>
<style scoped>
.cart_table .tooltip-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
}

.cart_table .tooltip-wrapper .info-icon-svg {
    width: 16px;
    height: 16px;
    margin-left: 5px;
    color: #888;
    transition: color 0.2s;
}

.cart_table .tooltip-wrapper:hover .info-icon-svg {
    color: #333;
}
.order-summary-drawer .tooltip-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
}

.order-summary-drawer .tooltip-wrapper .info-icon-svg {
    width: 16px;
    height: 16px;
    margin-left: 5px;
    color: #888;
    transition: color 0.2s;
}

.order-summary-drawer .tooltip-wrapper:hover .info-icon-svg {
    color: #333;
}

/* --- TOOLTIP STYLES FOR ORDER SUMMARY DRAWER --- */
.order-summary-drawer .tooltip-content {
    visibility: hidden;
    position: absolute;
    top: 100%;
    right: 0;
    transform: translateX(0);
    background: #fff;
    color: hsla(0, 0%, 7%, 1);
    padding: 0px;
    border-radius: 6px;
    z-index: 10;
    min-width: 392px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    margin-top: 8px;
    margin-bottom: 10px;
    border: 1px solid #ddd;
    opacity: 0;
    transition: opacity 0.3s;
    white-space: normal;
    font-size: 14px;
    z-index: 9999;
}

.order-summary-drawer .tooltip-content::after {
    content: none;
}

.order-summary-drawer .tooltip-content.is-visible {
    visibility: visible;
    opacity: 1;
}

.order-summary-drawer .tooltip-content.shipping-tooltip.is-visible {
    left: 0px;
    right: 0;
    top: 0;
    transform: translateY(-100%);
}

.order-summary-drawer .tooltip-content.packaging-tooltip.is-visible {
    left: 0px !important;
    right: 0;
    top: 68px !important;
    transform: translateY(-100%);
}

.order-summary-drawer .tooltip-content.sales-tax-tooltip.is-visible {
    left: 0px !important;
    right: 0;
    top: 0;
    transform: translateY(-100%);
}

.order-summary-drawer .tooltip-content .tooltip-header {
    padding: 9px 16px;
    background: hsla(204, 63%, 94%, 1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
}

.order-summary-drawer .tooltip-header h4 {
    font-weight: 600;
    font-size: 14px;
    line-height: 14px;
    color: hsla(209, 72%, 41%, 1);
    margin-bottom: 0px;
}

.order-summary-drawer .tooltip-header .close-icon {
    width: 18px;
    height: 18px;
    cursor: pointer;
    color: #666;
    flex-shrink: 0;
    transition: color 0.2s ease;
}

.order-summary-drawer .tooltip-header .close-icon:hover {
    color: #333;
}

.order-summary-drawer .tooltip-body {
    white-space: normal;
    padding: 16px;
}

.order-summary-drawer .tooltip-body ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.order-summary-drawer .tooltip-body li {
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    font-size: 14px;
}

.order-summary-drawer .tooltip-body li:last-child {
    margin-bottom: 0;
}

.order-summary-drawer .tooltip-body li.gray-out {
    color: #999;
}

.order-summary-drawer .tooltip-body li.gray-out span:last-child {
    color: #999;
}

/* Table styles for order summary drawer tooltip */
.order-summary-drawer .tooltip-content .base-fee-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
    font-size: 12px;
}

.order-summary-drawer .tooltip-content .base-fee-table thead {
    background-color: #f5f5f5;
}

.order-summary-drawer .tooltip-content .base-fee-table th {
    padding: 8px;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ddd;
    font-size: 12px;
    line-height: 1.4;
}

.order-summary-drawer .tooltip-content .base-fee-table th:nth-child(2),
.order-summary-drawer .tooltip-content .base-fee-table th:nth-child(3),
.order-summary-drawer .tooltip-content .base-fee-table th:nth-child(4),
.order-summary-drawer .tooltip-content .base-fee-table th:nth-child(5) {
    text-align: right;
}

.order-summary-drawer .tooltip-content .base-fee-table td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #eee;
    font-weight: normal;
    font-size: 12px;
    color: #333;
    line-height: 1.4;
}

.order-summary-drawer .tooltip-content .base-fee-table td:nth-child(2),
.order-summary-drawer .tooltip-content .base-fee-table td:nth-child(3),
.order-summary-drawer .tooltip-content .base-fee-table td:nth-child(4),
.order-summary-drawer .tooltip-content .base-fee-table td:nth-child(5) {
    text-align: right;
}

.order-summary-drawer .tooltip-content .base-fee-table tbody tr {
    border-bottom: 1px solid #eee;
}

.order-summary-drawer .tooltip-content .base-fee-table tbody tr.disable_data {
    color: #666;
}

.order-summary-drawer
    .tooltip-content
    .base-fee-table
    tbody
    tr.disable_data
    td {
    color: #666;
    font-weight: normal;
}

.order-summary-drawer .tooltip-content .base-fee-table tbody tr.total-row {
    border-top: 2px solid #ddd;
    border-bottom: none !important;
    background-color: #f5f5f5 !important;
    font-weight: bold;
}

.order-summary-drawer .tooltip-content .base-fee-table tbody tr.total-row td {
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: none !important;
}

/* Color box styles for order summary drawer */
.order-summary-drawer .tooltip-content .color-box-wrapper {
    display: inline-flex;
    align-items: center;
}

.order-summary-drawer .tooltip-content .color-box {
    width: 20px;
    height: 20px;
    border: 1px solid #ddd;
    border-radius: 3px;
    display: inline-block;
    cursor: pointer;
}

.order-summary-drawer .tooltip-content .product-base-price-row {
    font-weight: bold;
    background-color: #f9f9f9;
}

/* --- TOOLTIP STYLES FOR CART TABLE --- */
.cart_table .tooltip-content {
    visibility: hidden;
    position: absolute;
    top: -35px;
    left: 50%;
    transform: translateX(-50%);
    background: #fff;
    color: hsla(0, 0%, 7%, 1);
    padding: 0px;
    border-radius: 6px;
    z-index: 10;
    min-width: 392px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    margin-bottom: 10px;
    border: 1px solid #ddd;
    opacity: 0;
    transition: opacity 0.3s;
    white-space: normal;
    font-size: 14px;
    z-index: 9;
}

.tooltip-content::after {
    content: none;
}

.tooltip-content.is-visible {
    visibility: visible;
    opacity: 1;
}

.cart_table .table .tooltip-content .tooltip-header {
    padding: 9px 16px;
    background: hsla(204, 63%, 94%, 1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
}
.cart_table .table thead .tooltip-content th {
    padding: 8px;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ddd;
    font-size: 12px;
    background-color: #f5f5f5;
}
.cart_table .table tbody .tooltip-content td {
    padding: 8px !important;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ddd;
    font-size: 12px !important;
    height: auto;
}
.cart_table .table .tooltip-content thead {
    background-color: #f5f5f5;
}

/* Sub-items modal thead styles */
.cart_table .table thead th {
    padding: 8px;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ddd;
    font-size: 12px;
    background-color: #f5f5f5;
}

.cart_table .tooltip-header h4 {
    font-weight: 600;
    font-size: 14px;
    line-height: 14px;
    color: hsla(209, 72%, 41%, 1);
    margin-bottom: 0px;
}

.cart_table .tooltip-header .close-icon {
    width: 18px;
    height: 18px;
    cursor: pointer;
    color: #666;
    flex-shrink: 0;
    transition: color 0.2s ease;
}

.cart_table .tooltip-header .close-icon:hover {
    color: #333;
}

.cart_table .tooltip-body {
    white-space: normal;
    padding: 16px;
}

.cart_table .tooltip-body ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.cart_table .tooltip-body li {
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    font-size: 14px;
}

.cart_table .tooltip-body li:last-child {
    margin-bottom: 0;
}
.cart_table .tooltip-body li.gray-out {
    color: #999;
}

.cart_table .tooltip-body li.gray-out span:last-child {
    color: #999;
}

/* Table styles matching PCNAPriceBreakdown */
.cart_table .table .tooltip-content .base-fee-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
    font-size: 12px;
}

.cart_table .table .tooltip-content .base-fee-table thead {
    background-color: #f5f5f5;
}

.cart_table .table .tooltip-content .base-fee-table th {
    padding: 8px;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ddd;
    font-size: 12px;
    line-height: 1.4;
}

.cart_table .table .tooltip-content .base-fee-table th:nth-child(2),
.cart_table .table .tooltip-content .base-fee-table th:nth-child(3),
.cart_table .table .tooltip-content .base-fee-table th:nth-child(4) {
    text-align: right;
}

.cart_table .table .tooltip-content .base-fee-table td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #eee;
    font-weight: normal;
    font-size: 12px;
    color: #333;
    line-height: 1.4;
}

.cart_table .table .tooltip-content .base-fee-table td:nth-child(2),
.cart_table .table .tooltip-content .base-fee-table td:nth-child(3),
.cart_table .table .tooltip-content .base-fee-table td:nth-child(4) {
    text-align: right;
}

/* Sub-items modal tooltip table cells */
.cart_table .table .tooltip-content .base-fee-table td[data-v-2c020caf] {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #eee;
    font-weight: normal;
}

.cart_table
    .table
    .tooltip-content
    .base-fee-table
    td[data-v-2c020caf]:nth-child(2),
.cart_table
    .table
    .tooltip-content
    .base-fee-table
    td[data-v-2c020caf]:nth-child(3),
.cart_table
    .table
    .tooltip-content
    .base-fee-table
    td[data-v-2c020caf]:nth-child(4) {
    text-align: right;
}

/* Main items base fee table tbody tr */
.cart_table .table .tooltip-content .base-fee-table tbody tr {
    border-bottom: 1px solid #eee;
}

.cart_table .table .tooltip-content .base-fee-table tbody tr.disable_data {
    color: #666;
}

.cart_table .table .tooltip-content .base-fee-table tbody tr.disable_data td {
    color: #666;
    font-weight: normal;
}

.cart_table
    .table
    .tooltip-content
    .base-fee-table
    tbody
    tr.disable_data
    td[data-v-2c020caf] {
    color: #666;
    font-weight: normal;
}

.cart_table .table .tooltip-content .base-fee-table tbody tr.total-row {
    border-top: 2px solid #ddd;
    background-color: #f5f5f5 !important;
    font-weight: bold;
}

.cart_table .table .tooltip-content .base-fee-table tbody tr.total-row {
    border-bottom: none !important;
}

.cart_table .table .tooltip-content .base-fee-table tbody tr.total-row td {
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: none !important;
}

.cart_table
    .table
    .tooltip-content
    .base-fee-table
    tbody
    tr.total-row
    td[data-v-2c020caf] {
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: none !important;
}

/* Sub-items modal tooltip styles (same item code, different colors) */
.cart_table .modal .tooltip-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
}

.cart_table .modal .tooltip-wrapper .info-icon-svg {
    width: 16px;
    height: 16px;
    margin-left: 5px;
    color: #888;
    transition: color 0.2s;
}

.cart_table .modal .tooltip-wrapper:hover .info-icon-svg {
    color: #333;
}

.cart_table .modal .tooltip-content {
    visibility: hidden;
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #fff;
    color: hsla(0, 0%, 7%, 1);
    padding: 0px;
    border-radius: 6px;
    z-index: 10;
    min-width: 392px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    margin-bottom: 10px;
    border: 1px solid #ddd;
    opacity: 0;
    transition: opacity 0.3s;
    white-space: normal;
    font-size: 14px;
    z-index: 9999;
}

.cart_table .modal .tooltip-content.is-visible {
    visibility: visible;
    opacity: 1;
}

.cart_table .modal .tooltip-content .tooltip-header {
    padding: 9px 16px;
    background: hsla(204, 63%, 94%, 1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
}

.cart_table .modal .tooltip-header h4 {
    font-weight: 600;
    font-size: 14px;
    line-height: 14px;
    color: hsla(209, 72%, 41%, 1);
    margin-bottom: 0px;
}

.cart_table .modal .tooltip-header .close-icon {
    width: 18px;
    height: 18px;
    cursor: pointer;
    color: #666;
    flex-shrink: 0;
    transition: color 0.2s ease;
}

.cart_table .modal .tooltip-header .close-icon:hover {
    color: #333;
}

.cart_table .modal .tooltip-body {
    white-space: normal;
    padding: 16px;
}

.cart_table .modal .tooltip-content .base-fee-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
    font-size: 12px;
}

.cart_table .modal .tooltip-content .base-fee-table thead {
    background-color: #f5f5f5;
}

.cart_table .table thead th {
    padding: 8px;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ddd;
    font-size: 12px;
    background-color: #f5f5f5;
}

.cart_table .modal .tooltip-content .base-fee-table thead th {
    padding: 8px;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ddd;
    background-color: #f5f5f5;
    font-size: 12px;
}

.cart_table .modal .tooltip-content .base-fee-table th {
    padding: 8px;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ddd;
    font-size: 12px;
    line-height: 1.4;
}

.cart_table .modal .tooltip-content .base-fee-table th:nth-child(2),
.cart_table .modal .tooltip-content .base-fee-table th:nth-child(3),
.cart_table .modal .tooltip-content .base-fee-table th:nth-child(4) {
    text-align: right;
}

.cart_table .modal .tooltip-content .base-fee-table td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #eee;
    font-weight: normal;
    font-size: 12px;
    color: #333;
    line-height: 1.4;
}

.cart_table .modal .tooltip-content .base-fee-table td:nth-child(2),
.cart_table .modal .tooltip-content .base-fee-table td:nth-child(3),
.cart_table .modal .tooltip-content .base-fee-table td:nth-child(4) {
    text-align: right;
}

/* Sub-items modal base fee table td with scoped attribute */
.cart_table .modal .tooltip-content .base-fee-table td[data-v-2c020caf] {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #eee;
    font-weight: normal;
    font-size: 12px;
    color: #333;
    line-height: 1.4;
}

.cart_table
    .modal
    .tooltip-content
    .base-fee-table
    td[data-v-2c020caf]:nth-child(2),
.cart_table
    .modal
    .tooltip-content
    .base-fee-table
    td[data-v-2c020caf]:nth-child(3),
.cart_table
    .modal
    .tooltip-content
    .base-fee-table
    td[data-v-2c020caf]:nth-child(4) {
    text-align: right;
}

/* Sub-items modal base fee table tbody tr - matching main items */
.cart_table .modal .tooltip-content .base-fee-table tbody tr {
    border-bottom: 1px solid #eee;
}

/* Sub-items modal base fee table tbody with scoped attribute */
.cart_table .modal .tooltip-content .base-fee-table tbody[data-v-2c020caf] tr {
    border-bottom: 1px solid #eee;
}

/* Sub-items modal base fee table tr with scoped attribute */
.cart_table .modal .tooltip-content .base-fee-table tbody tr[data-v-2c020caf] {
    border-bottom: 1px solid #eee;
}

.cart_table .modal .tooltip-content .base-fee-table tbody tr.disable_data {
    color: #666;
}

.cart_table
    .modal
    .tooltip-content
    .base-fee-table
    tbody
    tr.disable_data[data-v-2c020caf] {
    color: #666;
}

.cart_table .modal .tooltip-content .base-fee-table tbody tr.disable_data td {
    color: #666;
    font-weight: normal;
    font-size: 12px;
}

.cart_table
    .modal
    .tooltip-content
    .base-fee-table
    tbody
    tr.disable_data
    td[data-v-2c020caf] {
    color: #666;
    font-weight: normal;
    font-size: 12px;
}

.cart_table .modal .tooltip-content .base-fee-table tbody tr.total-row {
    border-top: 2px solid #ddd;
    border-bottom: none !important;
    background-color: #f5f5f5 !important;
    font-weight: bold;
}

.cart_table
    .modal
    .tooltip-content
    .base-fee-table
    tbody
    tr.total-row[data-v-2c020caf] {
    border-top: 2px solid #ddd;
    border-bottom: none !important;
    background-color: #f5f5f5 !important;
    font-weight: bold;
}

.cart_table .modal .tooltip-content .base-fee-table tbody tr.total-row td {
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: none !important;
    font-size: 12px;
    font-weight: bold;
    color: #333;
}

.cart_table
    .modal
    .tooltip-content
    .base-fee-table
    tbody
    tr.total-row
    td[data-v-2c020caf] {
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: none !important;
    font-size: 12px;
    font-weight: bold;
    color: #333;
}

/* Color box styles */
.cart_table .tooltip-content .color-box-wrapper {
    display: inline-flex;
    align-items: center;
}

.cart_table .tooltip-content .color-box {
    width: 20px;
    height: 20px;
    border: 1px solid #ddd;
    border-radius: 3px;
    display: inline-block;
    cursor: pointer;
}

.cart_table .modal .tooltip-content .color-box-wrapper {
    display: inline-flex;
    align-items: center;
}

.cart_table .modal .tooltip-content .color-box {
    width: 20px;
    height: 20px;
    border: 1px solid #ddd;
    border-radius: 3px;
    display: inline-block;
    cursor: pointer;
}

.cart_table .tooltip-content .product-base-price-row {
    font-weight: bold;
    background-color: #f9f9f9;
}

.cart_table .modal .tooltip-content .product-base-price-row {
    font-weight: bold;
    background-color: #f9f9f9;
}
.order-summary-drawer .tooltip-body {
    max-height: 200px;
    overflow-y: auto;
}
/* Responsive */
@media (max-width: 768px) {
    .order-summary-drawer .order_prod_item_cost_breakdown .cost-item {
        gap: 12px;
    }

    .order-summary-drawer .order_prod_item_cost_breakdown .cost-label,
    .order-summary-drawer .order_prod_item_cost_breakdown .cost-value {
        font-size: 12px;
    }

    .order-summary-drawer
        .order_prod_item_cost_breakdown
        .cost-total
        .cost-value {
        font-size: 13px;
    }

    .order-summary-drawer .tooltip-content {
        min-width: 320px;
        font-size: 12px;
    }

    .order-summary-drawer .tooltip-content .base-fee-table {
        font-size: 11px;
    }

    .order-summary-drawer .tooltip-content .base-fee-table th,
    .order-summary-drawer .tooltip-content .base-fee-table td {
        padding: 6px;
        font-size: 11px;
    }
}
.order_prod_item_details
    .order_prod_item_cost_breakdown
    .cost-breakdown
    .cost-item.cost-total
    .cost-label
    span {
    font-weight: bold;
}
.cart_page_vthree .tooltip-body {
    max-height: 160px;
    overflow-y: auto;
}
.cart_page_vthree .cart_table .table thead th {
    position: relative;
}
</style>
